import React, { useState, useEffect } from 'react';
import { db, getSettings, calculateProductPricing } from '../lib/database';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Edit, Trash2, DollarSign, TrendingUp, Calculator } from 'lucide-react';

export default function ProductDetails({ product, onEdit, onDelete, onClose }) {
  const [settings, setSettings] = useState({});
  const [tempProfit, setTempProfit] = useState(product.profit);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settingsData = await getSettings();
      setSettings(settingsData);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await db.products.delete(product.id);
      onDelete();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Ürün silinirken hata oluştu!');
    }
  };

  const getTempPricing = () => {
    const tempProduct = { ...product, profit: tempProfit };
    return calculateProductPricing(tempProduct, settings);
  };

  const originalPricing = calculateProductPricing(product, settings);
  const tempPricing = getTempPricing();

  const formatCurrency = (amount, currency, isTL = false) => {
    const num = parseFloat(amount);
    let formatted;
    
    if (isTL) {
      // For TL conversions, remove decimals
      formatted = Math.round(num).toString();
    } else {
      // For original currency, remove .00 but keep other decimals
      formatted = num % 1 === 0 ? num.toString() : num.toFixed(2);
    }
    
    const getCurrencySymbol = (curr) => {
      switch(curr) {
        case 'USD': return '$';
        case 'EUR': return '€';
        case 'TL': return '₺';
        default: return curr;
      }
    };
    return `${getCurrencySymbol(currency)}${formatted}`;
  };



  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="items-center space-x-2">
              <span>{product.name}</span>
              {product.brand && <span className="text-muted-foreground">- {product.brand}</span>}
            </DialogTitle>
            <DialogDescription>
              {product.model && <span>Model: {product.model}</span>}
              {product.category && <span className="ml-2 text-primary">• {product.category}</span>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Product Image and Basic Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {product.image && (
                <div>
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-64 object-cover rounded-lg border"
                  />
                </div>
              )}

                          {/* Pricing Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calculator className="h-5 w-5" />
                  <span>Fiyat Detayları</span>
                </CardTitle>

              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Cost Breakdown */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Maliyet</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-end">
                        <span>Net Maliyet:</span>
                        <div className="text-right">
                          <div className="font-mono text-sm">
                            {formatCurrency(tempPricing.baseCost, tempPricing.currency)}
                          </div>
                          {tempPricing.currency !== 'TL' && (
                            <div className="text-xs text-muted-foreground">
                              {formatCurrency(tempPricing.baseCostInTl, 'TL', true)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-end">
                        <span>KDV (%{product.kdvPaid}):</span>
                        <div className="text-right">
                          <div className="font-mono text-sm">
                            {formatCurrency(tempPricing.kdvAmount, tempPricing.currency)}
                          </div>
                          {tempPricing.currency !== 'TL' && (
                            <div className="text-xs text-muted-foreground">
                              {formatCurrency(tempPricing.kdvAmountInTl, 'TL', true)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-end font-semibold border-t pt-2">
                        <span>Toplam Maliyet:</span>
                        <div className="text-right">
                          <div className="font-mono text-sm">
                            {formatCurrency(tempPricing.totalCost, tempPricing.currency)}
                          </div>
                          {tempPricing.currency !== 'TL' && (
                            <div className="text-xs text-muted-foreground">
                              {formatCurrency(tempPricing.totalCostInTl, 'TL', true)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sale Price Breakdown */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">Satış</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-end">
                        <span className="text-sm">Kar ({product.profitType === 'percentage' ? `%${tempProfit}` : `${tempProfit} ${tempPricing.currency}`}):</span>
                        <div className="text-right">
                          <div className="font-mono text-sm text-green-600">
                            {formatCurrency(tempPricing.profitAmount, tempPricing.currency)}
                          </div>
                          {tempPricing.currency !== 'TL' && (
                            <div className="text-xs text-muted-foreground">
                              {formatCurrency(tempPricing.profitAmountInTl, 'TL', true)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-end">
                        <span>Net Satış:</span>
                        <div className="text-right">
                          <div className="font-mono text-sm">
                            {formatCurrency(tempPricing.netSalePrice, tempPricing.currency)}
                          </div>
                          {tempPricing.currency !== 'TL' && (
                            <div className="text-xs text-muted-foreground">
                              {formatCurrency(tempPricing.netSalePriceInTl, 'TL', true)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-end">
                        <span className="text-sm">Satış KDV (%{settings.defaultKdvRate || 20}):</span>
                        <div className="text-right">
                          <div className="font-mono text-sm">
                            {formatCurrency(tempPricing.kdvCollected, tempPricing.currency)}
                          </div>
                          {tempPricing.currency !== 'TL' && (
                            <div className="text-xs text-muted-foreground">
                              {formatCurrency(tempPricing.kdvCollectedInTl, 'TL', true)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-end font-semibold border-t pt-2">
                        <span>Brüt Satış Fiyatı:</span>
                        <div className="text-right">
                          <div className="font-mono text-base text-primary">
                            {formatCurrency(tempPricing.grossSalePrice, tempPricing.currency)}
                          </div>
                          {tempPricing.currency !== 'TL' && (
                            <div className="text-xs text-muted-foreground">
                              {formatCurrency(tempPricing.grossSalePriceInTl, 'TL', true)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* KDV Summary */}
                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-2">KDV Özeti</h4>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="text-center">
                      <p className="text-muted-foreground text-xs mb-1">Ödenen KDV</p>
                      <div className="font-mono text-sm">
                        {formatCurrency(tempPricing.kdvAmount, tempPricing.currency)}
                      </div>
                      {tempPricing.currency !== 'TL' && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {formatCurrency(tempPricing.kdvAmountInTl, 'TL', true)}
                        </div>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-muted-foreground text-xs mb-1">Toplanan KDV</p>
                      <div className="font-mono text-sm">
                        {formatCurrency(tempPricing.kdvCollected, tempPricing.currency)}
                      </div>
                      {tempPricing.currency !== 'TL' && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {formatCurrency(tempPricing.kdvCollectedInTl, 'TL', true)}
                        </div>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-muted-foreground text-xs mb-1">Net KDV</p>
                      <div className="font-mono text-sm text-primary">
                        {formatCurrency(tempPricing.netKdv, tempPricing.currency)}
                      </div>
                      {tempPricing.currency !== 'TL' && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {formatCurrency(tempPricing.netKdvInTl, 'TL', true)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Profit Adjustment Slider */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <TrendingUp className="h-5 w-5" />
                    <span>Kar Marjı Önizleme</span>
                  </CardTitle>
                  {tempProfit !== product.profit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTempProfit(product.profit)}
                      className="flex items-center space-x-1"
                    >
                      <span className="text-xs">Sıfırla</span>
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="profit-slider">
                    Kar Marjı: {tempProfit} {product.profitType === 'percentage' ? '%' : 'TL'}
                  </Label>
                  <Slider
                    id="profit-slider"
                    min={product.profitType === 'percentage' ? 0 : 0}
                    max={product.profitType === 'percentage' ? 100 : 1000}
                    step={product.profitType === 'percentage' ? 1 : 10}
                    value={[tempProfit]}
                    onValueChange={(value) => setTempProfit(value[0])}
                    className="mt-2"
                  />
                </div>
                
              </CardContent>
            </Card>
              
              <div className="space-y-4">
                {product.specifications && product.specifications.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Özellikler</h3>
                    <div className="space-y-2">
                      {product.specifications.map((spec, index) => (
                        <div key={index} className="flex justify-between items-center py-1 border-b border-border/50">
                          <span className="font-medium text-sm">{spec.name}:</span>
                          <span className="text-sm text-muted-foreground">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {product.notes && (
                  <div>
                    <h3 className="font-semibold mb-2">Notlar</h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{product.notes}</p>
                  </div>
                )}
              </div>
            </div>


          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>
              Kapat
            </Button>
            <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Sil
            </Button>
            <Button onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Düzenle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <Dialog open={true} onOpenChange={() => setShowDeleteConfirm(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ürünü Sil</DialogTitle>
              <DialogDescription>
                "{product.name}" ürününü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                İptal
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Sil
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}